import React, { useState, useEffect, useRef } from "react";
import { securityAPI, logisticsAPI, usersAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { useAuth } from "../contexts/AuthContext";
import { Camera, Upload, Check, AlertTriangle, Package, Truck, UserCheck, Clock, X, Edit, Users } from "lucide-react";
import { fmtDateTime, fmtTime } from "../utils/dateUtils";

const VESSEL_SIZES = [
  { key: "count_5L",    label: "5 Litre" },
  { key: "count_10L",   label: "10 Litre" },
  { key: "count_20L",   label: "20 Litre" },
  { key: "count_30L",   label: "30 Litre" },
  { key: "count_40L",   label: "40 Litre" },
  { key: "egg_tray",    label: "Egg Tray" },
  { key: "banana_tray", label: "Banana Tray" },
];

function CameraCapture({ onCapture, label }) {
  const inputRef = useRef();
  const [preview, setPreview] = useState(null);

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
      onCapture(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <label className="text-xs font-bold uppercase text-[#8A7E73] mb-2 block">{label}</label>
      {preview ? (
        <div className="relative">
          <img src={preview} alt="captured" className="w-full h-32 object-cover rounded-lg border border-[#E5E1DB]" />
          <button onClick={() => { setPreview(null); onCapture(null); }}
            className="absolute top-2 right-2 bg-white rounded-full p-1 shadow text-[#C2624E] text-xs font-bold">✕</button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => { inputRef.current.capture = "environment"; inputRef.current.click(); }}
          className="w-full border-2 border-dashed border-[#E5E1DB] rounded-lg py-4 flex flex-col items-center gap-1 hover:border-[#E8956D] transition-colors text-[#8A7E73] hover:text-[#E8956D]"
        >
          <Camera size={20} />
          <span className="text-xs font-medium">Take Live Photo</span>
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
    </div>
  );
}

// ─── ENTRY LOG WITH VERIFICATION ─────────────────────────────────────────────
function EntryLog({ entries, onRefresh }) {
  const { user } = useAuth();
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [actionLoading, setActionLoading] = useState(null);

  const canVerify = user?.is_admin || ["admin", "super admin", "store keeper", "stores", "supervisor", "manager"].some(
    r => user?.role?.toLowerCase?.()?.includes(r)
  );

  const filteredEntries = entries.filter(e => {
    if (filterStatus === "pending") return e.verification_status === "pending_verification";
    if (filterStatus === "verified") return ["verified", "verified_with_changes", "auto_verified"].includes(e.verification_status);
    if (filterStatus === "rejected") return e.verification_status === "rejected";
    return true;
  }).filter(e => filterCategory === "all" || e.entry_category === filterCategory);

  const pendingCount = entries.filter(e => e.verification_status === "pending_verification").length;

  const handleVerify = async (id) => {
    setActionLoading(id);
    try {
      await securityAPI.verifyEntry(id);
      onRefresh();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setActionLoading(null);
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActionLoading(rejectModal.id);
    try {
      await securityAPI.rejectEntry(rejectModal.id, rejectReason);
      setRejectModal(null); setRejectReason("");
      onRefresh();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setActionLoading(null);
  };

  const handleReviseAndVerify = async () => {
    if (!editModal) return;
    setActionLoading(editModal.id);
    try {
      await securityAPI.reviseAndVerify(editModal.id, editForm);
      setEditModal(null); setEditForm({});
      onRefresh();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setActionLoading(null);
  };

  const getStatusBadge = (e) => {
    const st = e.verification_status;
    if (st === "pending_verification")
      return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FDF4EB] text-[#D98032] flex items-center gap-0.5 w-fit"><Clock size={9}/> Pending</span>;
    if (st === "verified" || st === "verified_with_changes")
      return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#EDF2EE] text-[#637C64] flex items-center gap-0.5 w-fit"><Check size={9}/> {st === "verified_with_changes" ? "Verified (edited)" : "Verified"}</span>;
    if (st === "rejected")
      return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FBEAE7] text-[#C2624E] flex items-center gap-0.5 w-fit"><X size={9}/> Rejected</span>;
    return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#EDF2EE] text-[#637C64] flex items-center gap-0.5 w-fit"><Check size={9}/> Auto</span>;
  };

  return (
    <div className="space-y-3">
      {/* Status filter tabs + refresh */}
      <div className="flex gap-2 items-center flex-wrap">
        {[
          { id: "all", label: "All" },
          { id: "pending", label: `Pending Verification${pendingCount > 0 ? ` (${pendingCount})` : ""}` },
          { id: "verified", label: "Verified" },
          { id: "rejected", label: "Rejected" },
        ].map(t => (
          <button key={t.id} onClick={() => setFilterStatus(t.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${filterStatus === t.id
              ? (t.id === "pending" && pendingCount > 0 ? "bg-[#D98032] text-white border-[#D98032]" : "bg-[#E8956D] text-[#3A322C] border-[#E8956D]")
              : "border-[#E5E1DB] text-[#5C5147]"}`}>
            {t.label}
          </button>
        ))}
        <button onClick={onRefresh} className="ml-auto text-xs text-[#E8956D] font-bold">↺ Refresh</button>
      </div>

      {/* Category filter */}
      <div className="flex gap-1.5 flex-wrap" data-testid="category-filter">
        {[
          { id: "all",              label: "All Types",      color: "bg-[#F4F3F0] text-[#5C5147]" },
          { id: "food_distribution",label: "Food Dist.",     color: "bg-blue-50 text-blue-700 border border-blue-200" },
          { id: "stores",           label: "Stores",         color: "bg-green-50 text-green-700 border border-green-200" },
          { id: "staff",            label: "Staff",          color: "bg-cyan-50 text-cyan-700 border border-cyan-200" },
          { id: "visitor",          label: "Visitor",        color: "bg-purple-50 text-purple-700 border border-purple-200" },
          { id: "others",           label: "Others",         color: "bg-gray-50 text-gray-600 border border-gray-200" },
        ].map(t => (
          <button key={t.id} onClick={() => setFilterCategory(t.id)}
            className={`px-2.5 py-1 rounded-full text-xs font-bold transition-all ${filterCategory === t.id ? t.color + " ring-2 ring-offset-1 ring-[#E8956D]" : t.color + " opacity-60 hover:opacity-100"}`}
            data-testid={`filter-cat-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {canVerify && pendingCount > 0 && (
        <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-xl px-4 py-3 text-sm text-[#D98032] font-medium flex items-center gap-2">
          <AlertTriangle size={14} />
          {pendingCount} stores entry/entries waiting for your verification
        </div>
      )}

      {/* Entry cards */}
      <div className="space-y-3">
        {filteredEntries.length === 0 && (
          <div className="text-center py-10 text-[#8A7E73] bg-white border border-[#E5E1DB] rounded-xl text-sm">No entries found</div>
        )}
        {filteredEntries.map(e => {
          const isPending = e.verification_status === "pending_verification";
          return (
            <div key={e.id} className={`bg-white border rounded-xl p-4 ${isPending ? "border-[#D98032]/40" : "border-[#E5E1DB]"}`} data-testid={`entry-${e.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  {/* Type + status row */}
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${e.entry_category === "food_distribution" ? "bg-blue-100 text-blue-700" : e.entry_category === "stores" ? "bg-green-100 text-green-700" : e.entry_category === "visitor" ? "bg-purple-100 text-purple-700" : e.entry_category === "staff" ? "bg-cyan-100 text-cyan-700" : "bg-gray-100 text-gray-700"}`}>
                      {e.entry_category?.replace("_", " ")}
                    </span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${e.direction === "outward" ? "bg-[#FDF4EB] text-[#D98032]" : "bg-[#EDF2EE] text-[#637C64]"}`}>{e.direction}</span>
                    {getStatusBadge(e)}
                    <span className="text-xs text-[#8A7E73] ml-auto">{fmtDateTime(e.entry_time)}</span>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-3">
                    {e.vendor_name && <div><p className="text-[#8A7E73]">Vendor</p><p className="font-medium text-[#3A322C]">{e.vendor_name}</p></div>}
                    {e.invoice_number && <div><p className="text-[#8A7E73]">Invoice No.</p><p className="font-mono font-bold text-[#5C5147]">{e.invoice_number}</p></div>}
                    {e.invoice_date && <div><p className="text-[#8A7E73]">Invoice Date</p><p className="font-medium text-[#3A322C]">{e.invoice_date}</p></div>}
                    {e.bill_weight && <div><p className="text-[#8A7E73]">Bill Wt.</p><p className="font-bold text-[#5C5147]">{e.bill_weight} kg</p></div>}
                    {e.actual_weight && <div><p className="text-[#8A7E73]">Actual Wt.</p><p className={`font-bold ${parseFloat(e.actual_weight) < parseFloat(e.bill_weight || 0) ? "text-[#C2624E]" : "text-[#637C64]"}`}>{e.actual_weight} kg</p></div>}
                    {e.vehicle_number && <div><p className="text-[#8A7E73]">Vehicle</p><p className="font-mono text-xs text-[#5C5147]">{e.vehicle_number}</p></div>}
                    {e.route_number && <div><p className="text-[#8A7E73]">Route</p><p className="font-bold text-[#E8956D]">Route {e.route_number}</p></div>}
                    {e.visitor_name && <div><p className="text-[#8A7E73]">Visitor</p><p className="font-medium text-[#3A322C]">{e.visitor_name}</p></div>}
                    {e.notes && <div className="col-span-2"><p className="text-[#8A7E73]">Notes</p><p className="text-[#5C5147]">{e.notes}</p></div>}
                  </div>

                  {/* Ticket footer: logged by + verified by */}
                  <div className="flex flex-wrap gap-3 text-[10px] text-[#8A7E73] border-t border-[#F4F3F0] pt-2 mt-1">
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#8A7E73]" />
                      Logged by <strong className="text-[#5C5147]">{e.created_by_name || "—"}</strong>
                    </span>
                    {e.verified_by && (
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#637C64]" />
                        Verified by <strong className="text-[#637C64]">{e.verified_by}</strong>
                        {e.verified_at && ` at ${fmtTime(e.verified_at)}`}
                      </span>
                    )}
                    {e.rejected_by && (
                      <span className="flex items-center gap-1 text-[#C2624E]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C2624E]" />
                        Rejected by <strong>{e.rejected_by}</strong>
                        {e.rejection_reason && ` — ${e.rejection_reason}`}
                      </span>
                    )}
                    {e.has_changes && <span className="text-[#D98032]">(details edited during verification)</span>}
                  </div>
                </div>

                {/* Stores verification actions — only for pending entries */}
                {isPending && canVerify && (
                  <div className="flex flex-col gap-2 shrink-0">
                    <button onClick={() => handleVerify(e.id)} disabled={actionLoading === e.id}
                      className="flex items-center gap-1.5 bg-[#637C64] text-white rounded-lg px-3 py-2 text-xs font-bold hover:bg-[#4e6350] disabled:opacity-60"
                      data-testid={`verify-btn-${e.id}`}>
                      <Check size={12} /> Verify
                    </button>
                    <button onClick={() => { setEditModal(e); setEditForm({ vendor_name: e.vendor_name, invoice_number: e.invoice_number, invoice_date: e.invoice_date, bill_weight: e.bill_weight, actual_weight: e.actual_weight, notes: e.notes, vehicle_number: e.vehicle_number }); }}
                      className="flex items-center gap-1.5 border border-blue-300 text-blue-700 bg-blue-50 rounded-lg px-3 py-2 text-xs font-bold hover:bg-blue-100"
                      data-testid={`edit-verify-btn-${e.id}`}>
                      <Edit size={12} /> Edit & Verify
                    </button>
                    <button onClick={() => { setRejectModal(e); setRejectReason(""); }}
                      className="flex items-center gap-1.5 border border-[#C2624E] text-[#C2624E] rounded-lg px-3 py-2 text-xs font-bold hover:bg-[#FBEAE7]"
                      data-testid={`reject-btn-${e.id}`}>
                      <X size={12} /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="font-bold text-[#3A322C]">Reject Entry</h3>
            <p className="text-sm text-[#8A7E73]">Entry from {rejectModal.created_by_name} — {rejectModal.vendor_name || rejectModal.entry_category}</p>
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Reason for rejection *</label>
              <textarea rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                placeholder="e.g. Invoice number does not match, wrong vendor..."
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setRejectModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleReject} disabled={!rejectReason.trim() || actionLoading}
                className="flex-1 bg-[#C2624E] text-white rounded-lg py-2.5 text-sm font-bold disabled:opacity-60">Confirm Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit & Verify Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white">
              <h3 className="font-bold text-[#3A322C]">Edit & Verify Entry</h3>
              <button onClick={() => setEditModal(null)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-xs text-[#8A7E73]">Logged by <strong>{editModal.created_by_name}</strong>. Correct any details below, then verify.</p>
              {[
                { key: "vendor_name", label: "Vendor Name" },
                { key: "invoice_number", label: "Invoice Number" },
                { key: "invoice_date", label: "Invoice Date", type: "date" },
                { key: "bill_weight", label: "Bill Weight (kg)", type: "number" },
                { key: "actual_weight", label: "Actual Weight (kg)", type: "number" },
                { key: "vehicle_number", label: "Vehicle Number" },
                { key: "notes", label: "Notes" },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">{f.label}</label>
                  <input type={f.type || "text"} step={f.type === "number" ? "0.01" : undefined}
                    className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                    value={editForm[f.key] ?? ""}
                    onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div className="flex gap-3 px-6 pb-6 sticky bottom-0 bg-white pt-3 border-t border-[#E5E1DB]">
              <button onClick={() => setEditModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleReviseAndVerify} disabled={actionLoading}
                className="flex-1 bg-[#637C64] text-white rounded-lg py-2.5 text-sm font-bold disabled:opacity-60 flex items-center justify-center gap-2">
                <Check size={14} /> Save Changes & Verify
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default function SecurityGate() {
  const { kitchens: ctxKitchens, selectedKitchen } = useKitchen();
  const { user } = useAuth();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  // Check if user can create gate entries (Route Coordinators cannot)
  const canCreateEntry = user?.is_admin || !!user?.permissions?.security_create;

  const BLANK_FORM = {
    entry_category: "food_distribution",
    direction: "inward",
    kitchen_id: selectedKitchen || "",
    route_id: "",
    vehicle_number: "",
    vendor_name: "",
    invoice_number: "",
    invoice_date: new Date().toISOString().split("T")[0],
    bill_weight: "",
    actual_weight: "",
    notes: "",
    vessel_counts: { count_5L: 0, count_10L: 0, count_20L: 0, count_30L: 0, count_40L: 0, egg_tray: 0, banana_tray: 0 },
  };

  const [routes, setRoutes] = useState([]);
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(BLANK_FORM);
  const [photos, setPhotos] = useState({});
  const [ocrData, setOcrData] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [activeView, setActiveView] = useState("form");
  const [validationError, setValidationError] = useState("");

  // Searchable route dropdown state
  const [routeSearch, setRouteSearch] = useState("");
  const [routeDropdownOpen, setRouteDropdownOpen] = useState(false);
  const routeDropdownRef = useRef();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (routeDropdownRef.current && !routeDropdownRef.current.contains(e.target)) {
        setRouteDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filteredRoutes = routes.filter(r => {
    if (!routeSearch) return true;
    return String(r.route_number).toLowerCase().includes(routeSearch.toLowerCase());
  });

  // Sync kitchens from context whenever it updates
  useEffect(() => {
    if (ctxKitchens && ctxKitchens.length > 0) {
      setKitchens(ctxKitchens);
    }
  }, [ctxKitchens]);

  // Sync form kitchen_id whenever selectedKitchen loads
  useEffect(() => {
    if (selectedKitchen && !form.kitchen_id) {
      setForm(f => ({ ...f, kitchen_id: selectedKitchen }));
    }
  }, [selectedKitchen]);

  useEffect(() => {
    fetchData();
    // Fallback: fetch kitchens directly if context is empty (e.g. first load on production)
    if (!ctxKitchens || ctxKitchens.length === 0) {
      usersAPI.getKitchens().then(r => {
        const ks = r.data || [];
        if (ks.length > 0) {
          setKitchens(ks);
          setForm(f => ({ ...f, kitchen_id: f.kitchen_id || ks[0].id }));
        }
      }).catch(() => {});
    }
  }, []);

  const fetchData = async () => {
    try {
      const r = await logisticsAPI.getRoutes();
      setRoutes(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const fetchEntries = async () => {
    try {
      const res = await securityAPI.getEntries({ limit: 20 });
      setEntries(res.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const handleRouteChange = (routeId) => {
    const route = routes.find(r => r.id === routeId);
    setForm(f => ({
      ...f,
      route_id: routeId,
      vehicle_number: route?.assigned_vehicle_number || ""
    }));
  };

  const handleOCR = async (b64) => {
    if (!b64) return;
    setOcrLoading(true);
    try {
      const res = await securityAPI.runOCR(b64);
      setOcrData(res.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setOcrLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setValidationError("");

    // Validate required fields for Stores inward
    if (isStores && form.direction === "inward") {
      const missing = [];
      if (!form.vendor_name?.trim()) missing.push("Vendor Name");
      if (!form.invoice_number?.trim()) missing.push("Invoice Number");
      if (!form.invoice_date) missing.push("Invoice Date");
      if (!form.bill_weight || parseFloat(form.bill_weight) <= 0) missing.push("Bill Weight");
      if (!form.actual_weight || parseFloat(form.actual_weight) <= 0) missing.push("Actual Weight");
      if (missing.length > 0) {
        setValidationError(`Required: ${missing.join(", ")}`);
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        // Convert empty strings to null for numeric fields
        bill_weight: form.bill_weight !== "" && form.bill_weight != null ? parseFloat(form.bill_weight) : null,
        actual_weight: form.actual_weight !== "" && form.actual_weight != null ? parseFloat(form.actual_weight) : null,
        photo_base64: photos.main || null,
        invoice_photo_base64: photos.invoice || null,
        weight_photo_base64: photos.weight || null,
      };
      await securityAPI.createEntry(payload);

      // Full form reset — prevent duplicate submission
      setForm({ ...BLANK_FORM, kitchen_id: selectedKitchen || "" });
      setPhotos({});
      setOcrData(null);
      setRouteSearch("");
      setRouteDropdownOpen(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);

      // Auto-refresh history if on history view
      fetchEntries();
    } catch (e) { setError(formatError(e)); }
    setLoading(false);
  };

  const isFoodDist = form.entry_category === "food_distribution";
  const isStores = form.entry_category === "stores";
  const isVisitor = form.entry_category === "visitor";
  const isStaff = form.entry_category === "staff";

  return (
    <div className="max-w-4xl space-y-6" data-testid="security-gate">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Security / Gate</h1>
        <div className="flex gap-2">
          {canCreateEntry && (
            <button onClick={() => setActiveView("form")} className={`px-4 py-2 rounded-lg text-sm font-bold ${activeView === "form" ? "bg-[#E8956D] text-[#3A322C]" : "border border-[#E5E1DB] text-[#5C5147]"}`}>
              New Entry
            </button>
          )}
          <button onClick={() => { setActiveView("history"); fetchEntries(); }} className={`px-4 py-2 rounded-lg text-sm font-bold ${activeView === "history" ? "bg-[#E8956D] text-[#3A322C]" : "border border-[#E5E1DB] text-[#5C5147]"}`}>
            Entry Log
          </button>
        </div>
      </div>

      {activeView === "form" && !canCreateEntry && (
        <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-xl px-4 py-4 text-sm text-[#D98032]">
          You don't have permission to create gate entries. Contact your admin to enable this access.
        </div>
      )}

      {activeView === "form" && canCreateEntry && (
        <form onSubmit={handleSubmit} className="bg-white border border-[#E5E1DB] rounded-2xl p-6 space-y-6 shadow-sm">
          {success && (
            <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-3 flex items-center gap-2 text-[#637C64]">
              <Check size={18} /> Entry recorded successfully! Form has been cleared.
            </div>
          )}
          {(error || validationError) && (
            <div className="bg-[#FBEAE7] border border-[#C2624E]/30 rounded-lg px-4 py-3 text-sm text-[#C2624E]">
              {validationError || error}
            </div>
          )}

          {/* Step 1: Category */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-[#8A7E73] mb-3 block">Entry Type *</label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { value: "food_distribution", label: "Food Distribution", icon: Truck },
                { value: "stores",            label: "Stores",            icon: Package },
                { value: "staff",             label: "Staff In / Out",    icon: Users },
                { value: "visitor",           label: "Visitor",           icon: UserCheck },
                { value: "others",            label: "Others",            icon: AlertTriangle },
              ].map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, entry_category: value }))}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${form.entry_category === value ? "border-[#E8956D] bg-[#E8956D]/10" : "border-[#E5E1DB] hover:border-[#E8956D]/50"}`}
                  data-testid={`entry-type-${value}`}
                >
                  <Icon size={22} className={form.entry_category === value ? "text-[#E8956D]" : "text-[#8A7E73]"} />
                  <span className="text-xs font-bold text-[#3A322C] text-center">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Direction */}
          {(isStores || isFoodDist) && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-[#8A7E73] mb-2 block">Direction *</label>
              <div className="flex gap-3">
                {["inward", "outward"].map(d => (
                  <button key={d} type="button"
                    onClick={() => setForm(f => ({ ...f, direction: d }))}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-bold border-2 transition-all capitalize ${form.direction === d ? "border-[#E8956D] bg-[#E8956D]/10 text-[#E8956D]" : "border-[#E5E1DB] text-[#5C5147]"}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Kitchen — auto-populated; only show selector if user has multiple kitchens */}
          <div>
            <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Kitchen *</label>
            {kitchens.length <= 1 ? (
              <div className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm bg-[#F4F3F0] text-[#5C5147] font-medium">
                {kitchens.find(k => k.id === form.kitchen_id)?.name || "Auto-assigned"}
              </div>
            ) : (
              <select
                className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                value={form.kitchen_id}
                onChange={e => setForm(f => ({ ...f, kitchen_id: e.target.value }))}
                required
              >
                <option value="">Select Kitchen</option>
                {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            )}
          </div>

          {/* Food Distribution fields */}
          {isFoodDist && (
            <>
              <div ref={routeDropdownRef}>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Route Number</label>
                <div className="relative">
                  <input
                    type="text"
                    className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="Type route number to search..."
                    value={routeSearch}
                    onChange={e => { setRouteSearch(e.target.value); setRouteDropdownOpen(true); }}
                    onFocus={() => setRouteDropdownOpen(true)}
                    autoComplete="off"
                    data-testid="route-search-input"
                  />
                  {form.route_id && (
                    <button
                      type="button"
                      onClick={() => { setRouteSearch(""); setRouteDropdownOpen(false); setForm(f => ({ ...f, route_id: "", vehicle_number: "" })); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8A7E73] hover:text-[#C2624E] text-xs font-bold"
                    >✕</button>
                  )}
                  {routeDropdownOpen && filteredRoutes.length > 0 && (
                    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-[#E5E1DB] rounded-lg shadow-lg max-h-52 overflow-y-auto">
                      {filteredRoutes.map(r => (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => {
                            handleRouteChange(r.id);
                            setRouteSearch(`Route ${r.route_number}`);
                            setRouteDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#F4F3F0] flex items-center justify-between ${form.route_id === r.id ? "bg-[#E8956D]/10 font-bold text-[#E8956D]" : "text-[#3A322C]"}`}
                        >
                          <span><span className="font-bold text-[#E8956D]">Route {r.route_number}</span>{r.coordinator_name ? ` — ${r.coordinator_name}` : ""}</span>
                          {r.driver_vehicle_number && <span className="text-xs text-[#8A7E73] font-mono">{r.driver_vehicle_number}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  {routeDropdownOpen && routeSearch && filteredRoutes.length === 0 && (
                    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-[#E5E1DB] rounded-lg shadow-lg px-4 py-3 text-sm text-[#8A7E73]">
                      No routes found for "{routeSearch}"
                    </div>
                  )}
                </div>
                {form.route_id && (
                  <p className="mt-1 text-xs text-[#637C64] font-medium">
                    ✓ Selected: Route {routes.find(r => r.id === form.route_id)?.route_number}
                    {routes.find(r => r.id === form.route_id)?.coordinator_name && ` — ${routes.find(r => r.id === form.route_id)?.coordinator_name}`}
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle Number (auto-populated)</label>
                <input
                  className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] bg-[#FAFAF8]"
                  value={form.vehicle_number}
                  onChange={e => setForm(f => ({ ...f, vehicle_number: e.target.value }))}
                  placeholder="Vehicle number"
                  data-testid="vehicle-number-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-3 block">Vessel Count</label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {VESSEL_SIZES.map(({ key, label }) => (
                    <div key={key} className="text-center">
                      <label className="text-xs text-[#8A7E73] block mb-1">{label}</label>
                      <input
                        type="number"
                        min="0"
                        className="w-full border border-[#E5E1DB] rounded-lg px-2 py-2 text-sm text-center outline-none focus:border-[#E8956D]"
                        value={form.vessel_counts?.[key] === 0 ? "" : (form.vessel_counts?.[key] || "")}
                        onChange={e => setForm(f => ({ ...f, vessel_counts: { ...f.vessel_counts, [key]: e.target.value === "" ? 0 : (parseInt(e.target.value) || 0) } }))}
                        placeholder="0"
                        data-testid={`vessel-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <CameraCapture label="Gate Photo" onCapture={b64 => setPhotos(p => ({ ...p, main: b64 }))} />
            </>
          )}

          {/* Stores fields */}
          {isStores && (
            <>
              {/* Required fields banner */}
              {form.direction === "inward" && (
                <div className="bg-[#FDF4EB] border border-[#D98032]/20 rounded-xl px-4 py-2.5 text-xs text-[#D98032] font-medium">
                  Fields marked * are mandatory for Stores Inward entry
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">
                    Vendor Name {form.direction === "inward" && <span className="text-[#C2624E]">*</span>}
                  </label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="e.g. Sri Sai Traders"
                    value={form.vendor_name || ""} onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value }))}
                    data-testid="vendor-name" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle Number</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.vehicle_number || ""} onChange={e => setForm(f => ({ ...f, vehicle_number: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">
                    Invoice Number {form.direction === "inward" && <span className="text-[#C2624E]">*</span>}
                  </label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="e.g. INV-2024-001"
                    value={form.invoice_number || ""} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))}
                    data-testid="invoice-number" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">
                    Invoice Date {form.direction === "inward" && <span className="text-[#C2624E]">*</span>}
                  </label>
                  <input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.invoice_date || new Date().toISOString().split("T")[0]}
                    onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))}
                    data-testid="invoice-date" />
                </div>
              </div>
              <CameraCapture label={`Invoice Photo ${form.direction === "inward" ? "*" : "(optional)"} (for OCR)`} onCapture={b64 => { setPhotos(p => ({ ...p, invoice: b64 })); if (b64) handleOCR(b64); }} />
              {ocrLoading && <p className="text-sm text-[#E8956D] animate-pulse">Scanning invoice...</p>}
              {ocrData && !ocrData.error && (
                <div className="bg-[#F4F3F0] rounded-lg p-4">
                  <p className="text-xs font-bold uppercase text-[#8A7E73] mb-2">OCR Auto-filled</p>
                  <p className="text-sm font-semibold text-[#3A322C]">{ocrData.vendor_name}</p>
                  <p className="text-xs text-[#5C5147]">Invoice: {ocrData.invoice_number} | Total: ₹{ocrData.total_amount}</p>
                  {ocrData.items?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {ocrData.items.slice(0, 5).map((item, i) => (
                        <p key={`idx-${i}`} className="text-xs text-[#5C5147]">{item.name}: {item.quantity} {item.unit} @ ₹{item.rate}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <CameraCapture label="Weight/Stock Photo (optional)" onCapture={b64 => setPhotos(p => ({ ...p, weight: b64 }))} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">
                    Bill Weight (kg) {form.direction === "inward" && <span className="text-[#C2624E]">*</span>}
                  </label>
                  <input type="number" step="0.01" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="0.00"
                    value={form.bill_weight || ""} onChange={e => setForm(f => ({ ...f, bill_weight: e.target.value }))}
                    data-testid="bill-weight" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">
                    Actual Weight (kg) {form.direction === "inward" && <span className="text-[#C2624E]">*</span>}
                  </label>
                  <input type="number" step="0.01" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="0.00"
                    value={form.actual_weight || ""} onChange={e => setForm(f => ({ ...f, actual_weight: e.target.value }))}
                    data-testid="actual-weight" />
                </div>
              </div>
            </>
          )}

          {/* Staff Entry/Exit */}
          {isStaff && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                <p className="text-xs font-bold text-blue-700 uppercase mb-0.5">Staff In / Out</p>
                <p className="text-xs text-[#5C5147]">Record staff entry and exit separately from visitor log.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Staff Member Name *</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="Full name of staff member"
                    value={form.staff_name || ""} onChange={e => setForm(f => ({ ...f, staff_name: e.target.value }))}
                    data-testid="staff-name" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Staff ID / Employee No (optional)</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="e.g. EMP-042"
                    value={form.staff_id || ""} onChange={e => setForm(f => ({ ...f, staff_id: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle Number (if any)</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="e.g. TS07UB0497"
                    value={form.vehicle_number || ""} onChange={e => setForm(f => ({ ...f, vehicle_number: e.target.value }))} />
                </div>
              </div>
            </>
          )}

          {/* Visitor Entry/Exit */}
          {isVisitor && (
            <>
              <div className="bg-[#EDF2EE] border border-[#637C64]/20 rounded-xl px-4 py-3">
                <p className="text-xs font-bold text-[#637C64] uppercase mb-0.5">Visitor Entry / Exit</p>
                <p className="text-xs text-[#5C5147]">Record visitor details for gate access control and audit trail.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Visitor Name *</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="Full name of visitor"
                    value={form.visitor_name || ""} onChange={e => setForm(f => ({ ...f, visitor_name: e.target.value }))}
                    data-testid="visitor-name" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Phone Number</label>
                  <input type="tel" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="Mobile number"
                    value={form.visitor_phone || ""} onChange={e => setForm(f => ({ ...f, visitor_phone: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Organisation</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="Company / Department"
                    value={form.visitor_organisation || ""} onChange={e => setForm(f => ({ ...f, visitor_organisation: e.target.value }))} />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Purpose of Visit *</label>
                  <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.visitor_purpose || ""} onChange={e => setForm(f => ({ ...f, visitor_purpose: e.target.value }))}
                    data-testid="visitor-purpose">
                    <option value="">Select purpose...</option>
                    {["Government Inspection", "Supplier / Vendor", "Maintenance / Repair", "Delivery", "NGO / Partner Visit", "Media / Press", "Audit", "Other"].map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">ID Proof Type</label>
                  <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.id_proof_type || ""} onChange={e => setForm(f => ({ ...f, id_proof_type: e.target.value }))}>
                    <option value="">Select...</option>
                    {["Aadhaar", "PAN Card", "Driving Licence", "Passport", "Voter ID", "Employee ID"].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle Number (if any)</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="e.g. TS07UB0497"
                    value={form.vehicle_number || ""} onChange={e => setForm(f => ({ ...f, vehicle_number: e.target.value }))} />
                </div>
              </div>
              <CameraCapture label="Visitor Photo (optional)" onCapture={b64 => setPhotos(p => ({ ...p, main: b64 }))} />
            </>
          )}

          {/* Others */}
          {form.entry_category === "others" && (
            <>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle Number</label>
                <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.vehicle_number || ""} onChange={e => setForm(f => ({ ...f, vehicle_number: e.target.value }))} />
              </div>
              <CameraCapture label="Document/Item Photo" onCapture={b64 => setPhotos(p => ({ ...p, main: b64 }))} />
            </>
          )}

          <div>
            <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label>
            <textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none" value={form.notes || ""} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#E8956D] text-[#3A322C] hover:bg-[#D48661] transition-colors rounded-lg py-3 font-bold shadow-sm disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="submit-entry-btn"
          >
            {loading ? <div className="w-5 h-5 border-2 border-[#3A322C]/30 border-t-[#3A322C] rounded-full animate-spin" /> : <><Check size={18} /> Record Entry</>}
          </button>
        </form>
      )}

      {activeView === "history" && (
        <EntryLog entries={entries} onRefresh={fetchEntries} />
      )}
    </div>
  );
}

